import { ChangeDetectionStrategy, Component, ElementRef, forwardRef, HostListener, Input, OnInit, ViewChild } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { applyLimits, caretFromTokens, computeLocaleSeps, countTokensBeforeCaret, normalizeIncomingDecimal, sanitize, splitDecimal, updateLimitHint } from '@shared/helpers/numeric-input.helper';

@Component({
    selector: 'custom-numeric-string-input',
    templateUrl: './custom-numeric-string-input.component.html',
    styleUrls: ['./custom-numeric-string-input.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => CustomNumericStringInputComponent),
            multi: true,
        },
    ],
})
export class CustomNumericStringInputComponent implements ControlValueAccessor, OnInit {
    @ViewChild('inputEl', { static: true }) inputEl: ElementRef<HTMLInputElement>;

    @Input() locale: string = 'it-IT';
    @Input() allowNegative = true;
    @Input() textEnd: boolean = true;
    @Input() maxFractionDigits = 2;
    @Input() maxIntegerDigits = 15;
    @Input() placeholder = 'Inserisci';
    @Input() disabled: boolean;

    limitHint: string | null = null;

    private onChange: (v: string | null) => void = () => {};
    private onTouched: () => void = () => {};
    private _value: string | null = null;
    private _decimalSep: '.' | ',' = ',';
    private _groupSep: string = '.';

    ngOnInit(): void {
        const thousandSeparator = computeLocaleSeps(this.locale);
        this._decimalSep = thousandSeparator.decimalSep;
        this._groupSep = thousandSeparator.groupSep;
    }

    writeValue(value: string | null): void {
        const v = value?.toString().trim() ?? '';

        if (!v) {
            this._value = null;
            this._setNativeValue('');
            return;
        }

        const normalized = normalizeIncomingDecimal(v, this._decimalSep, this._groupSep);
        const cleaned = this._cleanTyping(normalized);
        const limited = applyLimits(cleaned, this.maxIntegerDigits, this.maxFractionDigits, this._decimalSep);

        this._value = limited === '-' || limited === '' ? null : limited;
        const formatted = this._value ? this._formatWithGrouping(this._value) : '';
        this._setNativeValue(formatted);
    }

    registerOnChange(fn: (v: string | null) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
        if (this.inputEl?.nativeElement) {
            this.inputEl.nativeElement.disabled = isDisabled;
        }
    }

    @HostListener('input', ['$event'])
    onInput(ev: Event): void {
        const el = ev.target as HTMLInputElement;
        const raw = el.value ?? '';
        const caret = el.selectionStart ?? raw.length;
        const tokenCount = countTokensBeforeCaret(raw, caret, this._decimalSep);
        const cleaned = this._cleanTyping(raw);
        const limited = applyLimits(cleaned, this.maxIntegerDigits, this.maxFractionDigits, this._decimalSep);

        this.limitHint = updateLimitHint(limited, this.maxIntegerDigits);
        if (el.value !== limited) {
            el.value = limited;
            const newCaret = caretFromTokens(limited, tokenCount, this._decimalSep);
            queueMicrotask(() => el.setSelectionRange(newCaret, newCaret));
        }

        const committable = !(this.maxFractionDigits > 0 && limited.endsWith(this._decimalSep));
        if (committable) {
            this._value = limited === '-' ? null : limited;
            this.onChange(this._value);
        }
    }

    @HostListener('focusin')
    onFocusIn(): void {
        if (!this._value) {
            return;
        }

        this._setNativeValue(this._value);
    }

    @HostListener('focusout')
    onFocusOut(): void {
        this.onTouched();
        if (this._value === null) {
            this._setNativeValue('');
            return;
        }

        let v = this._value;
        if (this.maxFractionDigits > 0 && v.endsWith(this._decimalSep)) {
            v = v.slice(0, -1);
        }

        v = applyLimits(this._cleanTyping(v), this.maxIntegerDigits, this.maxFractionDigits, this._decimalSep);
        this._value = v === '-' ? null : v;
        this.onChange(this._value);
        const formatted = this._value ? this._formatWithGrouping(this._value) : '';
        this._setNativeValue(formatted);
    }

    private _setNativeValue(v: string): void {
        if (this.inputEl?.nativeElement) {
            this.inputEl.nativeElement.value = v;
        }
    }

    private _cleanTyping(value: string): string {
        const { negative, body } = sanitize(value, this.allowNegative, this._groupSep, this._decimalSep, this.maxFractionDigits);
        const { intPart, fracPart, separatorChar } = splitDecimal(body, this._decimalSep);
        const sign = negative ? '-' : '';
        if (!separatorChar) {
            return `${sign}${intPart}`;
        }

        return `${sign}${intPart}${separatorChar}${fracPart}`;
    }

    private _formatWithGrouping(v: string): string {
        const sign = v.startsWith('-') ? '-' : '';
        const body = sign ? v.slice(1) : v;
        const { intPart, fracPart, separatorChar } = splitDecimal(body, this._decimalSep);
        const intGrouped = this._groupSep ? (intPart || '0').replace(/\B(?=(\d{3})+(?!\d))/g, this._groupSep) : intPart || '0';
        if (!separatorChar) {
            return `${sign}${intGrouped}`;
        }

        return `${sign}${intGrouped}${separatorChar}${fracPart ?? ''}`;
    }
}
